import Card from "components/Card/Card";
import { ControlledInput } from "components/Card/ControlledInput";
import { Checkbox, MaskedInput, TextInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import {
  getFilteredServiceOptions,
  ServiceMultiSelect,
} from "components/ServiceMultiSelect/ServiceMultiSelect";
import LibertyMutualReferralFields from "components/LibertyMutualReferralFields";
import { LIBERTY_FUTURE_MEDICAL_ALLOCATION_SERVICE_OPTION } from "constants/LibertyMutualConstants";
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";

import { SERVICE_TYPES, STATES } from "../shared/IntakeConstants";
import type { FormData, IntakeDocumentType } from "../shared/IntakeTypes";
import AckEmailRecipientsForm from "./AckEmailRecipientsForm";
import type { DocumentUploadIntakeRef } from "./DocumentUploadIntake";
import DocumentUploadIntake from "./DocumentUploadIntake";

// Matches File.referring_party_name / Adjuster.name (both 255) so copying
// Referring Party Name into adjusterName while the checkbox is checked
// never has to truncate.
const ADJUSTER_NAME_MAX_LENGTH = 255;

// Basic format check, not full RFC 5322 -- matches the intent of the
// browser's own type="email" validation on AckEmailRecipientsForm's inputs.
// Blank rows are fine (not required); only a non-empty malformed entry
// fails validation.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validateAckEmailRecipients = (value: string[] = []) => {
  const hasInvalidEmail = value.some(
    (email) => email.trim() !== "" && !EMAIL_PATTERN.test(email.trim()),
  );
  return !hasInvalidEmail || "Enter a valid email address";
};

type DocumentUploadAndAdjusterIntakeProps = {
  handleFieldChange: (
    field: string,
    value:
      | IntakeDocumentType[]
      | string
      | string[]
      | number
      | boolean
      | Array<{ value: string; label: string }>,
  ) => Promise<void>;
  intakeIdentifier: string | null;
  onUploadStateChange?: (isUploading: boolean, count: number) => void;
  initializeSubmission: () => Promise<string | null | undefined>;
  isLibertyUser: boolean;
};

const DocumentUploadAndAdjusterIntake = forwardRef<
  DocumentUploadIntakeRef,
  DocumentUploadAndAdjusterIntakeProps
>(
  (
    {
      handleFieldChange,
      intakeIdentifier,
      onUploadStateChange,
      initializeSubmission,
      isLibertyUser,
    },
    ref,
  ) => {
    const {
      control,
      setValue,
      getValues,
      register,
      watch,
      trigger,
      formState: { errors },
    } = useFormContext<FormData>();

    const [sameAsReferringParty, setSameAsReferringParty] = useState(false);

    // watch for changes to the checkbox value
    const watchedSameAsReferringParty = watch(
      "adjuster_same_as_referring_party",
    );
    const selectedServices = watch("services") || [];
    const referringPartyName = watch("referring_party_name") || "";
    const additionalAckEmailRecipients =
      watch("additional_ack_email_recipients") || [];

    useEffect(() => {
      setSameAsReferringParty(!!watchedSameAsReferringParty);
    }, [watchedSameAsReferringParty]);

    // Registered here (not tied to a native input) so "Next" can trigger
    // format validation the same way it does for the Liberty referral
    // fields on this same page.
    useEffect(() => {
      register("additional_ack_email_recipients", {
        validate: validateAckEmailRecipients,
      });
    }, [register]);

    // While the checkbox is active, Adjuster fields (including Name) are
    // hidden. Keep adjusterName in form state equal to Referring Party Name
    // so submit still sends the right value; Referring Party Name remains
    // the editable source (also used for the ack salutation / Liberty subject).
    useEffect(() => {
      if (!sameAsReferringParty) return;

      const nextAdjusterName = referringPartyName.slice(
        0,
        ADJUSTER_NAME_MAX_LENGTH,
      );
      if (getValues("adjusterName") !== nextAdjusterName) {
        setValue("adjusterName", nextAdjusterName);
        void handleFieldChange("adjusterName", nextAdjusterName);
      }
    }, [
      sameAsReferringParty,
      referringPartyName,
      getValues,
      setValue,
      handleFieldChange,
    ]);

    const handleSameAsReferringPartyChange = async (checked: boolean) => {
      setSameAsReferringParty(checked);
      await handleFieldChange("adjuster_same_as_referring_party", checked);
    };

    const handleServicesChange = useCallback(
      async (options: Array<{ value: string; label: string }>) => {
        await handleFieldChange("services", options);
        // Trigger validation for the services field after change
        trigger("services");
      },
      [handleFieldChange, trigger],
    );

    const serviceOptions = useMemo(
      () =>
        isLibertyUser
          ? [...SERVICE_TYPES, LIBERTY_FUTURE_MEDICAL_ALLOCATION_SERVICE_OPTION]
          : SERVICE_TYPES,
      [isLibertyUser],
    );

    const filteredServiceOptions = useMemo(() => {
      if (!selectedServices.length) return serviceOptions;

      return getFilteredServiceOptions(selectedServices, serviceOptions);
    }, [selectedServices, serviceOptions]);

    // Left column: Liberty (optional) → Service Selection.
    // Right column: Add Files (top) → Referring Party → Adjuster. Each
    // column is one grid item so gl-2 stacks cards within the column
    // instead of interleaving.
    return (
      <>
        <div>
          {isLibertyUser && (
            <Card heading="Liberty Mutual Referral Information" cardNumber={1}>
              <LibertyMutualReferralFields
                control={control}
                errors={errors}
                onFieldSave={handleFieldChange}
                required
              />
            </Card>
          )}
          <Card heading="Service Selection" cardNumber={3}>
            <Controller
              control={control}
              name="services"
              render={({ field: { value } }) => (
                <ServiceMultiSelect
                  label="Services Selected"
                  name="services"
                  errors={errors}
                  options={filteredServiceOptions}
                  value={value}
                  onChange={handleServicesChange}
                  required={false}
                />
              )}
            />
            {/* Plain register() is enough for these checkboxes: the intake
                form has no draft persistence, and handleFieldChange is just
                setValue, which register already does on change. They are
                read off form state on final submission. */}
            <Checkbox {...register("is_24_hour_rush")} label="24 Hour Rush" />
            <Checkbox {...register("is_3_day_rush")} label="3 Day Rush" />
            <Checkbox
              {...register("is_update_to_prior_msa")}
              label="Is this referral an update to a prior MSA?"
            />
          </Card>
        </div>
        <div>
          <DocumentUploadIntake
            ref={ref}
            handleFieldChange={handleFieldChange}
            intakeIdentifier={intakeIdentifier}
            onUploadStateChange={onUploadStateChange}
            initializeSubmission={initializeSubmission}
          />
          <Card heading="Referring Party" cardNumber={isLibertyUser ? 2 : 1}>
            <ControlledInput
              control={control}
              name="referring_party_name"
              label="Referring Party Name"
              errors={errors}
              maxLength={255}
              onSave={(value) =>
                handleFieldChange("referring_party_name", value)
              }
              componentType={TextInput}
            />
            <AckEmailRecipientsForm
              initialEmails={additionalAckEmailRecipients}
              onChange={(emails) =>
                void handleFieldChange(
                  "additional_ack_email_recipients",
                  emails,
                )
              }
            />
          </Card>
          <Card heading="Adjuster Information" cardNumber={5}>
            {/* Checking hides every Adjuster field (including Name) and
                copies Referring Party Name into adjusterName in form state.
                Contact fields other than name are still filled from the
                logged-in user on submit when the flag is true. */}
            <Checkbox
              {...register("adjuster_same_as_referring_party")}
              label="Same As Referring Party"
              onChange={(e) =>
                handleSameAsReferringPartyChange(e.target.checked)
              }
            />
            {!sameAsReferringParty && (
              <>
                <ControlledInput
                  control={control}
                  name="adjusterName"
                  label="Adjuster Name"
                  errors={errors}
                  maxLength={ADJUSTER_NAME_MAX_LENGTH}
                  onSave={(value) => handleFieldChange("adjusterName", value)}
                  componentType={TextInput}
                />
                <ControlledInput
                  control={control}
                  name="adjusterEmail"
                  label="Adjuster Email"
                  errors={errors}
                  maxLength={40}
                  onSave={(value) => handleFieldChange("adjusterEmail", value)}
                  componentType={TextInput}
                />
                <ControlledInput
                  control={control}
                  name="adjusterStreet"
                  label="Adjuster Street Address"
                  errors={errors}
                  maxLength={40}
                  onSave={(value) => handleFieldChange("adjusterStreet", value)}
                  componentType={TextInput}
                />
                <ControlledInput
                  control={control}
                  name="adjusterApt"
                  label="Adjuster Apt/Suite"
                  errors={errors}
                  maxLength={40}
                  onSave={(value) => handleFieldChange("adjusterApt", value)}
                  componentType={TextInput}
                />
                <ControlledInput
                  control={control}
                  name="adjusterCity"
                  label="Adjuster City"
                  errors={errors}
                  maxLength={40}
                  onSave={(value) => handleFieldChange("adjusterCity", value)}
                  componentType={TextInput}
                />
                <ControlledSelect<FormData>
                  control={control}
                  name="adjusterState"
                  label="Adjuster State"
                  errors={errors}
                  options={STATES}
                  onSave={async (option) => {
                    if (option) {
                      setValue("adjusterState", String(option.value));
                      await handleFieldChange("adjusterState", option.value);
                    }
                  }}
                />
                <ControlledInput
                  control={control}
                  name="adjusterZipCode"
                  label="Adjuster Zip Code"
                  errors={errors}
                  maxLength={11}
                  onSave={(value) =>
                    handleFieldChange("adjusterZipCode", value)
                  }
                  componentType={MaskedInput}
                  mask="zipcode"
                />
                <ControlledInput
                  control={control}
                  name="adjusterPhone"
                  label="Adjuster Phone"
                  errors={errors}
                  maxLength={40}
                  onSave={(value) => handleFieldChange("adjusterPhone", value)}
                  componentType={MaskedInput}
                  mask="phone"
                />
              </>
            )}
          </Card>
        </div>
      </>
    );
  },
);

DocumentUploadAndAdjusterIntake.displayName = "DocumentUploadAndAdjusterIntake";

export default DocumentUploadAndAdjusterIntake;
